import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Wrapper } from "./style";
import { MakeApiCall } from "../../../apis";
import {
    message,
    notification,
    Table,
    Button,
    ConfigProvider,
    Pagination,
    Menu,
    Upload,
    Dropdown,
    Collapse,
    Spin,
    Tooltip
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import { fakeProImage } from "../../../config/fakeData";
import { CloseOutlined, DownloadOutlined, PlusCircleOutlined, FileTextOutlined, CloudUploadOutlined, InfoCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import AddCogs from "./lib/addCogs";
import EditableCell from "./lib/EditableCell";
import SyncToast from '../../../components/toast/SyncToast';
import { useProductTemplateExport } from "../../../hooks/UseProductTemplateExport";
import { extractSkusFromImportFile } from "../../../utils/ExportProducttemplate";
import { useHubSpotSync } from "../../../hooks/useHubSpotSync";
import SetupStepsBanner from "../../../components/onboarding/Setupstepsbanner";
import FeatureLockedOverlay from "../../../components/modal/Featurelockedoverlay";
import { useCanEdit } from "../../../contexts/SubUserAccessContext";
import { getAmount } from "../../../utils/helper";

const { Panel } = Collapse;
const AMAZON_BASE_URL = "https://www.amazon.in/dp/";
const SYNC_IN_PROGRESS_STATUSES = new Set(["queued", "syncing", "processing", "in_progress", "running", "pending"]);
const SYNC_STATUS_POLL_INTERVAL_MS = 3000;

const isSyncInProgress = (data) => {
    const status = String(data?.sync_status || data?.status || "").toLowerCase();
    return SYNC_IN_PROGRESS_STATUSES.has(status);
};

const Products = (props) => {
    const { userData } = props;
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
    const isSpApiConnected = userData?.user?.user_data?.is_amz_sp_api_connected === 1;
    const canEdit = useCanEdit();
    const canMutateProducts = isSpApiConnected && canEdit;
    const isDataSyncing = isSpApiConnected && userData?.user?.user_data?.is_data_sync_email_sent === null;

    const currencySymbol = userData?.user?.user_data?.connected_marketplace?.currency_symbol;
    const selectedCurrencySign = currencySymbol || '₹';

    const [totalPage, setTotalPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [editingCell, setEditingCell] = useState({ key: null, field: null });
    const [openPanels, setOpenPanels] = useState({});
    const [syncToastDismissed, setSyncToastDismissed] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [updatedSkus, setUpdatedSkus] = useState([]);
    const [syncingProducts, setSyncingProducts] = useState(false);
    const [syncPollTick, setSyncPollTick] = useState(0);
    const [showSyncReminderBanner, setShowSyncReminderBanner] = useState(false);
    const { syncActionTimestamp } = useHubSpotSync(userData);

    const { downloadTemplate, templateLoading } = useProductTemplateExport(userData);

    const searchDebounceRef = useRef(null);
    const prevSearchRef = useRef("");
    const syncPollTimeoutRef = useRef(null);
    const confirmedSyncingRef = useRef(false);
    const assumeSyncingOnNextPollRef = useRef(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        return () => {
            if (syncPollTimeoutRef.current) clearTimeout(syncPollTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        if (isDataSyncing) setSyncToastDismissed(false);
    }, [isDataSyncing]);

    const getProductList = useCallback(async (data) => {
        if (!isSpApiConnected) { setLoading(false); return; }
        setLoading(true);
        const response = await MakeApiCall(
            `get-products?search_text=${data?.searchText ?? searchText}&page=${data?.page || page || 1}&per-page=${data?.pageSize || pageSize}`,
            "get", null, true, {}, userData?.token
        );
        if (response?.status) {
            setTotalPage(response?.data?.pagination?.totalCount || 0);
            setList(response?.data?.records || []);
        } else {
            message.warning(response?.message);
            setList([]);
        }
        setLoading(false);
    }, [isSpApiConnected, userData?.token, searchText, page, pageSize]);

    const trackUpdatedSku = useCallback((sku) => {
        if (!sku) return;
        setUpdatedSkus((prev) => (prev.includes(sku) ? prev : [...prev, sku]));
    }, []);

    const trackUpdatedSkus = useCallback((skus) => {
        if (!Array.isArray(skus) || skus.length === 0) return;
        setUpdatedSkus((prev) => {
            const next = [...prev];
            skus.forEach((sku) => {
                if (sku && !next.includes(sku)) next.push(sku);
            });
            return next;
        });
    }, []);

    const triggerSyncReminderBanner = () => {
        setShowSyncReminderBanner(true);
    };

    const updateField = useCallback(async (record, field, newValue) => {
        if (!canMutateProducts) return;
        const finalValue = field === "tags" ? newValue : parseFloat(newValue);
        const response = await MakeApiCall(`update-product/${record.id}`, "post", { [field]: finalValue }, true, null, userData?.token);
        if (response?.status) {
            message.success("Updated successfully!");
            trackUpdatedSku(record?.sku);
            triggerSyncReminderBanner();
            getProductList();
        } else {
            message.error(response?.message || "Update failed");
        }
    }, [canMutateProducts, userData?.token, getProductList, trackUpdatedSku]);

    const pollSyncStatus = useCallback(async () => {
        if (!isSpApiConnected) {
            setSyncingProducts(false);
            confirmedSyncingRef.current = false;
            return false;
        }

        const keepIfAlreadySyncing = assumeSyncingOnNextPollRef.current;
        if (keepIfAlreadySyncing) {
            assumeSyncingOnNextPollRef.current = false;
        }

        try {
            const response = await MakeApiCall(
                "dashboard-optimized/sync-status",
                "get",
                null,
                true,
                {},
                userData?.token
            );

            const apiInProgress = response?.status && response?.data
                ? isSyncInProgress(response.data)
                : false;

            if (apiInProgress) {
                confirmedSyncingRef.current = true;
            } else if (confirmedSyncingRef.current) {
                message.success("Sync complete.");
                confirmedSyncingRef.current = false;
                getProductList();
            }

            const nextSyncing = apiInProgress || keepIfAlreadySyncing;
            setSyncingProducts(nextSyncing);
            return nextSyncing;
        } catch (error) {
            console.error("Products sync status error:", error);
            return false;
        }
    }, [isSpApiConnected, userData?.token, getProductList]);

    useEffect(() => {
        if (!isSpApiConnected) {
            setSyncingProducts(false);
            confirmedSyncingRef.current = false;
            return;
        }

        let cancelled = false;

        const runPoll = async () => {
            if (cancelled) return;

            const inProgress = await pollSyncStatus();
            if (cancelled) return;

            if (inProgress) {
                syncPollTimeoutRef.current = setTimeout(runPoll, SYNC_STATUS_POLL_INTERVAL_MS);
            }
        };

        runPoll();

        return () => {
            cancelled = true;
            if (syncPollTimeoutRef.current) {
                clearTimeout(syncPollTimeoutRef.current);
                syncPollTimeoutRef.current = null;
            }
        };
    }, [isSpApiConnected, pollSyncStatus, syncPollTick]);

    const handleProductsSync = async () => {
        if (!canMutateProducts || updatedSkus.length === 0 || syncingProducts) return;
        setShowSyncReminderBanner(false);
        try {
            const response = await MakeApiCall(
                "dashboard-optimized/sync",
                "post",
                { sku: updatedSkus },
                true,
                null,
                userData?.token
            );
            if (response?.status) {
                message.success(response?.message || "Sync started successfully.");
                setUpdatedSkus([]);
                setSyncingProducts(true);
                assumeSyncingOnNextPollRef.current = true;
                setSyncPollTick((tick) => tick + 1);
            } else {
                message.error(response?.message || "Failed to sync products.");
            }
        } catch (error) {
            console.error("Products sync error:", error);
            message.error("Something went wrong while syncing products.");
        }
    };

    const hasUnsyncedChanges = updatedSkus.length > 0;
    const syncButtonDisabled = !canMutateProducts || (!hasUnsyncedChanges && !syncingProducts);
    const syncTooltipTitle = !isSpApiConnected
        ? "Connect your Amazon account to enable sync."
        : syncingProducts
            ? "Sync is in progress."
            : !hasUnsyncedChanges
                ? "Update any product field to enable sync."
                : "";

    useEffect(() => {
        if (!isSpApiConnected) { setLoading(false); setList([]); return; }
        const isSearchChange = prevSearchRef.current !== searchText;
        prevSearchRef.current = searchText;
        if (isSearchChange) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = setTimeout(() => {
                getProductList({ page: 1, pageSize, searchText });
            }, 400);
            return () => clearTimeout(searchDebounceRef.current);
        }
        getProductList({ page, pageSize, searchText });
    }, [page, pageSize, isSpApiConnected, searchText]);

    const handleStartEdit = useCallback((record, field) => {
        if (!canMutateProducts) return;
        setEditingCell({ key: record.id, field });
    }, [canMutateProducts]);

    const handleCancelEdit = useCallback(() => {
        setEditingCell({ key: null, field: null });
    }, []);

    const handleSaveEdit = useCallback(async (record, field, draftValue) => {
        setEditingCell({ key: null, field: null });
        await updateField(record, field, draftValue);
    }, [updateField]);

    const renderEditableCell = useCallback((record, field, value) => (
        <EditableCell
            record={record}
            field={field}
            value={value}
            isSpApiConnected={canMutateProducts}
            isEditing={editingCell.key === record.id && editingCell.field === field}
            onStartEdit={handleStartEdit}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
        />
    ), [editingCell, canMutateProducts, handleStartEdit, handleSaveEdit, handleCancelEdit]);

    const colTitle = (label, tip) => (
        <div className="d-flex align-items-center">
            {label}
            <Tooltip title={tip} placement="top">
                <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '12px', cursor: 'help', flexShrink: 0 }} />
            </Tooltip>
        </div>
    );

    const handleEditCogs = useCallback((record) => {
        setSelectedProduct(record);
        setIsModalVisible(true);
    }, []);

    const columns = useMemo(() => [
        { title: "#", key: "index", fixed: 'left', width: 50, render: (text, record, index) => (page - 1) * pageSize + index + 1 },
        {
            title: "Product Details", key: "productDetails", fixed: 'left', width: 300,
            render: (_, record) => (
                <div className="d-flex align-items-center bdr-left-tbl products-product-details-cell rounded-4 p-3 py-2">
                    <div className="symbol symbol-65px overflow-hidden me-3">
                        <img src={record?.item_image || fakeProImage} alt="" />
                    </div>
                    <div className="d-flex flex-column">
                        <a
                            className="text-gray-800 fs-6 text-hover-danger mb-1 fw-bolder text-one min-w-200px"
                            href={record?.asin ? `${AMAZON_BASE_URL}${record.asin}` : "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {record?.item_name || ""}
                        </a>
                        <div className="fs-7 mt-1"><span><b>SKU:</b> {record?.sku}</span> / <span><b>ASIN:</b> {record?.asin}</span></div>
                        <div className="fs-7 mt-1"><span><b>{getAmount(record?.item_price, selectedCurrencySign)}</b></span> / <span> {record?.item_type}</span></div>
                    </div>
                </div>
            ),
        },
        { title: colTitle("Tags", "Tags let you group and filter SKUs for easier analysis — e.g., by category, season, or campaign."), dataIndex: "tags", key: "tags", render: (value, record) => renderEditableCell(record, "tags", value) },
        {
            title: colTitle("COGS", "Cost of goods sold per unit — used to calculate true profit per order."), dataIndex: "item_cogs", key: "item_cogs",
            render: (cogs, record) => (
                <div className="d-flex align-items-center px-2">
                    {cogs ? getAmount(cogs, selectedCurrencySign) : "N/A"}
                    <Link
                        to=""
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (canMutateProducts) handleEditCogs(record); }}
                        style={{ marginLeft: "10px" }}
                        className={`products-cogs-link ${canMutateProducts ? "" : "products-cogs-link--disabled"}`}
                    >
                        <i className="ki-outline ki-plus-circle" />
                    </Link>
                </div>
            ),
            sorter: (a, b) => a.item_cogs - b.item_cogs
        },
        { title: colTitle("Replenishment Time (Days)", "Average days needed to restock this SKU — helps plan inventory before you run out."), dataIndex: "replenishment_time", key: "replenishment_time", render: (value, record) => renderEditableCell(record, "replenishment_time", value), sorter: (a, b) => a.replenishment_time - b.replenishment_time },
        { title: colTitle("FBM Shipping Cost", "Per-order shipping cost for FBM fulfillment — deducted from FBM order revenue."), dataIndex: "fbm_shipping_cost_per_package", key: "fbm_shipping_cost_per_package", render: (value, record) => renderEditableCell(record, "fbm_shipping_cost_per_package", value), sorter: (a, b) => a.fbm_shipping_cost_per_package - b.fbm_shipping_cost_per_package },
        { title: colTitle("Val. of Unsellable Return (%)", "% of returned units deemed unsellable — used to estimate loss from damaged/unusable returns."), dataIndex: "unsellable_returns_percentage", key: "unsellable_returns_percentage", render: (value, record) => renderEditableCell(record, "unsellable_returns_percentage", value), sorter: (a, b) => a.unsellable_returns_percentage - b.unsellable_returns_percentage },
        { title: colTitle("Tax Paid on COGS (%)", "Input tax paid on procurement — used to calculate your net tax credit."), dataIndex: "product_tax_code", key: "product_tax_code", render: (value, record) => renderEditableCell(record, "product_tax_code", value), sorter: (a, b) => a.product_tax_code - b.product_tax_code },
        { title: colTitle("Tax Collected on Sales (%)", "Tax charged to buyers on your sales — used to calculate your net tax liability."), dataIndex: "vat_rate", key: "vat_rate", render: (value, record) => renderEditableCell(record, "vat_rate", value), sorter: (a, b) => a.vat_rate - b.vat_rate }
    ], [page, pageSize, canMutateProducts, isSpApiConnected, renderEditableCell, handleEditCogs, selectedCurrencySign]);

    const ProductMobileCard = ({ record }) => {
        const panelKey = `tax-${record.id}`;
        const isEditingInPanel = editingCell.key === record.id && ["unsellable_returns_percentage", "product_tax_code", "vat_rate"].includes(editingCell.field);
        return (
            <div className="card border mb-4 shadow-none rounded-3 overflow-hidden products-mobile-card">
                <div className="card-header py-3 px-4 products-mobile-card__header">
                    <div className="d-flex align-items-center w-100">
                        <img src={record?.item_image || fakeProImage} alt="" style={{ width: 50, height: 50, borderRadius: 6, marginRight: 12, objectFit: 'contain', background: 'var(--app-surface)' }} />
                        <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                            <a className="products-mobile-card__title fw-bolder text-hover-danger text-truncate mb-0 fs-7" href={record?.asin ? `${AMAZON_BASE_URL}${record.asin}` : "#"} target="_blank" rel="noopener noreferrer">{record?.item_name}</a>
                            <div className="fs-9 text-muted"><b>SKU:</b> {record?.sku} / <b>ASIN:</b> {record?.asin}</div>
                        </div>
                    </div>
                </div>
                <div className="card-body p-4">
                    <div className="row g-4">
                        <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">Tags</label><div>{renderEditableCell(record, "tags", record.tags)}</div></div>
                        <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">COGS</label><div className="d-flex align-items-center fs-8 ps-2 mt-1">{record.item_cogs ? getAmount(record.item_cogs, selectedCurrencySign) : "N/A"}<PlusCircleOutlined className={`ms-2 ${canMutateProducts ? "text-danger" : "text-muted"}`} style={{ opacity: canEdit ? 1 : 0.45, cursor: canMutateProducts ? "pointer" : "not-allowed" }} onClick={(e) => { e.stopPropagation(); if (canMutateProducts) handleEditCogs(record); }} /></div></div>
                        <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">Replenishment Time (Days)</label><div>{renderEditableCell(record, "replenishment_time", record.replenishment_time)}</div></div>
                        <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">FBM shipping cost</label><div>{renderEditableCell(record, "fbm_shipping_cost_per_package", record.fbm_shipping_cost_per_package)}</div></div>
                    </div>
                </div>
                <Collapse
                    ghost
                    activeKey={(openPanels[panelKey] || isEditingInPanel) ? ['1'] : []}
                    onChange={(keys) => { if (!isEditingInPanel) setOpenPanels(prev => ({ ...prev, [panelKey]: keys.includes('1') })); }}
                    className="border-top products-mobile-collapse"
                >
                    <Panel header={<span className="fs-9 fw-bold text-muted text-uppercase">Tax & Return Details</span>} key="1">
                        <div className="row g-4 px-2 pb-3">
                            <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">Val. of unsellable return (%)</label><div>{renderEditableCell(record, "unsellable_returns_percentage", record.unsellable_returns_percentage)}</div></div>
                            <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">Tax Paid on COGS (%)</label><div>{renderEditableCell(record, "product_tax_code", record.product_tax_code)}</div></div>
                            <div className="col-6"><label className="fs-9 text-muted text-uppercase fw-bold mb-1">Tax Collected on Sales (%)</label><div>{renderEditableCell(record, "vat_rate", record.vat_rate)}</div></div>
                        </div>
                    </Panel>
                </Collapse>
            </div>
        );
    };

    const handleCloseModal = () => { setIsModalVisible(false); setSelectedProduct(null); };
    const handleSearch = (e) => { setSearchText(e.target.value); setPage(1); };

    const handleUpload = (file) => {
        if (!canMutateProducts) return false;
        const rawFile = file.originFileObj || file;
        setUploadedFile(rawFile);
        return false; // prevent antd auto-upload
    };

    const handleApplyImport = async () => {
        if (!canMutateProducts || !uploadedFile || uploading) return;
        syncActionTimestamp("sv_used_import_export");
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);
            const response = await MakeApiCall(
                "products/upload-import", "post", formData, true, {}, userData?.token
            );
            if (response?.status) {
                const fileId = response.data?.file_id ?? response.data?.export_file_id ?? "";
                const key = `import-notif-${Date.now()}`;
                notification.success({
                    key,
                    message: "Import started",
                    description: (
                        <span>
                            {fileId && <><b>{fileId}</b> — </>}
                            {response.message ?? "Your file is being processed."}{" "}
                            <a
                                href="/settings/file-manager?tab=import"
                                style={{ color: "#f45b68", fontWeight: 600 }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    notification.destroy(key);
                                    navigate("/settings/file-manager?tab=import");
                                }}
                            >
                                Go to Import & Export
                            </a>
                        </span>
                    ),
                    duration: 0,
                    placement: "top",
                    style: { width: "max-content" }
                });
                const importedSkus = Array.isArray(response.data?.sku)
                    ? response.data.sku
                    : await extractSkusFromImportFile(uploadedFile);
                trackUpdatedSkus(importedSkus);
                triggerSyncReminderBanner();
                setUploadedFile(null);
                getProductList();
            } else {
                message.error(response?.message || "Import failed. Please try again.");
            }
        } catch (err) {
            console.error("Products import error:", err);
            message.error("Something went wrong while uploading the file.");
        } finally {
            setUploading(false);
        }
    };

    const menu = (
        <Menu className="p-2 shadow-sm products-import-menu" style={{ borderRadius: '8px', minWidth: '220px' }}>
            <div className="px-3 py-2 border-bottom">
                <span className="fw-bold text-gray-800 fs-7">Bulk Import Products</span>
            </div>
            <Menu.Item key="1" className="py-3" disabled={!canMutateProducts || templateLoading} onClick={downloadTemplate}>
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex flex-column">
                        <span className="fs-7 fw-bold">1. Download Template</span>
                        <span className="text-muted" style={{ fontSize: '10px' }}>Fetch all product records</span>
                    </div>
                    {templateLoading ? <Spin size="small" /> : <DownloadOutlined className="text-danger fs-5" />}
                </div>
            </Menu.Item>
            <Menu.Divider />
            <div className="px-3 py-2">
                <span className="fs-7 fw-bold d-block mb-2">2. Upload Updated File</span>
                <Upload
                    beforeUpload={handleUpload}
                    fileList={uploadedFile ? [uploadedFile] : []}
                    onRemove={() => setUploadedFile(null)}
                    maxCount={1}
                    disabled={!canMutateProducts}
                >
                    <Button
                        icon={<CloudUploadOutlined />}
                        disabled={!canMutateProducts}
                        className="w-100 fs-7 h-40px border-dashed border-danger text-danger bg-light-danger bg-opacity-10"
                    >
                        {uploadedFile ? 'Change File' : 'Choose File'}
                    </Button>
                </Upload>
                {uploadedFile && (
                    <Button
                        size="small"
                        type="primary"
                        danger
                        block
                        className="mt-2"
                        onClick={handleApplyImport}
                        loading={uploading}
                        disabled={uploading || !canMutateProducts}
                    >
                        {uploading ? 'Processing...' : 'Apply Import'}
                    </Button>
                )}
            </div>
        </Menu>
    );

    return (
        <Wrapper>
            <Helmet><title>Product</title></Helmet>
            <SetupStepsBanner userData={userData} />
            <style>{`.editable-cell-container:hover { background-color: var(--app-surface-alt); border: 1px dashed #f45b68; }`}</style>

            <SyncToast
                visible={isDataSyncing && !syncToastDismissed}
                onDismiss={() => setSyncToastDismissed(true)}
            />

            <div style={{ position: 'relative' }}>
                <FeatureLockedOverlay locked={!isSpApiConnected} title="Features Locked" message="Connect your Amazon account to unlock analytics."/>
                <div className="mb-10 mx-md-n9 mx-n2 px-10 py-5 filter products-search-wrap">
                    <div className="position-relative">
                        <i className="ki-outline ki-magnifier products-search-icon fs-2 position-absolute top-50 translate-middle-y ms-4" />
                        <input
                            type="text"
                            value={searchText}
                            onChange={handleSearch}
                            disabled={!isSpApiConnected}
                            className={`form-control form-control-solid border fs-7 ps-12 all-search products-search-input ${isMobile ? 'w-100' : 'w-300px'}`}
                            placeholder="Search sku, asin, title..."
                        />
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-header border-bottom-dashed">
                                <h3 className="card-title fw-bolder">Products List</h3>
                                <div className="card-toolbar">
                                    <div className="d-flex align-items-center gap-2">
                                        <Tooltip title={syncButtonDisabled ? syncTooltipTitle : ""} placement="top">
                                            <span>
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm fs-7 fw-bold border border-danger border-opacity-25 products-sync-btn ${
                                                        syncingProducts
                                                            ? "products-sync-btn--syncing btn-danger text-white"
                                                            : hasUnsyncedChanges && canMutateProducts
                                                                ? "btn-danger text-white"
                                                                : "btn-light-danger products-sync-btn--disabled"
                                                    }`}
                                                    onClick={handleProductsSync}
                                                    disabled={syncButtonDisabled && !syncingProducts}
                                                    aria-busy={syncingProducts}
                                                >
                                                    {syncingProducts ? (
                                                        <>
                                                            <SyncOutlined spin className="me-1 products-sync-icon" />
                                                            Syncing
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-arrow-repeat me-1 products-sync-icon" /> Sync
                                                        </>
                                                    )}
                                                </button>
                                            </span>
                                        </Tooltip>
                                        <Dropdown overlay={menu} trigger={["click"]} disabled={!canMutateProducts} placement="bottomRight">
                                            <button className="btn btn-sm btn-light-danger fs-7 fw-bold border border-danger border-opacity-25" disabled={!canMutateProducts}>
                                                <i className="bi bi-download me-1"/> Import Products
                                            </button>
                                        </Dropdown>
                                    </div>
                                </div>
                            </div>

                            {/* ── Export-started banner ──────────────────────────────────── */}

                            <div className="card-body py-2">
                                {showSyncReminderBanner && (
                                    <div className="d-flex align-items-center justify-content-between py-3 px-4 mb-4 bg-light-success border border-success border-opacity-25 rounded">
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-check-circle-fill text-success fs-5 me-2" />
                                            <span className="fw-semibold fs-7 text-gray-900">
                                                Just updated your products? Sync now to see changes instantly.
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-link text-gray-600 p-0 border-0 shadow-none"
                                            aria-label="Dismiss"
                                            onClick={() => setShowSyncReminderBanner(false)}
                                        >
                                            <CloseOutlined style={{ fontSize: 14 }} />
                                        </button>
                                    </div>
                                )}
                                {loading ? (
                                    <div className="text-center py-10"><Spin size="large" /></div>
                                ) : (
                                    isMobile
                                        ? <div className="p-2">{list.map(item => <ProductMobileCard key={item.id} record={item} />)}</div>
                                        : (
                                            <div className="table-responsive">
                                                <Table
                                                    scroll={{ x: "max-content" }}
                                                    columns={columns}
                                                    dataSource={list}
                                                    rowKey="id"
                                                    pagination={false}
                                                    components={{
                                                        table: (p) => <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7 products-page-table" {...p} />,
                                                        header: { row: (p) => <tr {...p} className="fw-bolder products-table-header-row bg-cus" /> }
                                                    }}
                                                />
                                            </div>
                                        )
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gy-5 g-xl-5">
                    <div className="col-xxl-12">
                        <div className="card card-xxl-stretch mb-5 mb-xl-8">
                            <div className="card-body py-2">
                                <ConfigProvider theme={{ token: { colorPrimary: "#f45b68" } }}>
                                    <Pagination
                                        className="mt-3"
                                        current={page}
                                        pageSize={pageSize}
                                        total={totalPage}
                                        disabled={!isSpApiConnected}
                                        showSizeChanger
                                        responsive={true}
                                        simple={isMobile}
                                        onChange={(p, s) => { setPage(p); setPageSize(s); }}
                                        showTotal={(t, r) => isMobile ? `Total: ${t}` : `${r[0]}-${r[1]} of ${t} items`}
                                    />
                                </ConfigProvider>
                            </div>
                        </div>
                    </div>
                </div>

                <AddCogs
                    visible={isModalVisible}
                    onClose={handleCloseModal}
                    product={selectedProduct}
                    userData={userData}
                    selectedCurrencySign={selectedCurrencySign}
                    getList={() => {
                        trackUpdatedSku(selectedProduct?.sku);
                        triggerSyncReminderBanner();
                        getProductList();
                    }}
                    isUpdate={!!(selectedProduct?.item_cogs)}
                    readOnly={!canEdit}
                />
            </div>
        </Wrapper>
    );
};

export default Products;